import React from "react";
import styled, { css } from "styled-components";

// ─── Size tokens ────────────────────────────────────────────────────────────
const SIZE = {
  xs: {
    height: 24,
    fontSize: 9,
    paddingH: 10,
    iconPaddingH: 5,
    gap: 4,
    iconSize: 14,
  },
  sm: {
    height: 32,
    fontSize: 12,
    paddingH: 14,
    iconPaddingH: 8,
    gap: 6,
    iconSize: 20,
  },
  md: {
    height: 36,
    fontSize: 14,
    paddingH: 16,
    iconPaddingH: 10,
    gap: 7,
    iconSize: 24,
  },
  lg: {
    height: 48,
    fontSize: 18,
    paddingH: 22,
    iconPaddingH: 14,
    gap: 9,
    iconSize: 24,
  },
};

// ─── Styled button ────────────────────────────────────────────────────────────
const StyledButton = styled.button`
  /* Layout */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
  white-space: nowrap;

  /* Pill shape */
  border-radius: 9999px;
  cursor: pointer;

  /* Transition */
  transition:
    filter 120ms ease,
    background-color 120ms ease;

  /* Typography */
  font-family: var(--cc-font-family-base);
  font-weight: 570;
  letter-spacing: 0.01em;
  line-height: 1;

  /* ── Size ── */
  ${({ $size, $iconOnly }) => {
    const s = SIZE[$size] || SIZE.md;
    const ph = $iconOnly ? s.iconPaddingH : s.paddingH;
    return css`
      height: ${s.height}px;
      font-size: ${s.fontSize}px;
      gap: ${s.gap}px;
      padding: 0 ${ph}px;
      ${$iconOnly ? `width: ${s.height}px; padding: 0;` : ""}
    `;
  }}

  /* ── Variant: filled ── */
  ${({ $variant, $color, $textColor }) =>
    $variant === "filled" &&
    css`
      background-color: ${$color};
      border: none;
      color: ${$textColor || "#ffffff"};
      &:hover:not(:disabled) {
        filter: brightness(1.1);
      }
      &:active:not(:disabled) {
        filter: brightness(0.92);
      }
    `}

  /* ── Variant: outline ── */
  ${({ $variant, $color }) =>
    $variant === "outline" &&
    css`
      background-color: transparent;
      border: 1.5px solid ${$color};
      color: ${$color};
      &:hover:not(:disabled) {
        background-color: ${$color}18;
      }
      &:active:not(:disabled) {
        background-color: ${$color}30;
      }
    `}

  /* ── Variant: ghost ── */
  ${({ $variant, $textColor }) =>
    $variant === "ghost" &&
    css`
      background-color: transparent;
      border: none;
      color: ${$textColor || (({ theme }) => theme.colorOnBackground)};
      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colorSurfaceVariant};
      }
      &:active:not(:disabled) {
        background-color: ${({ theme }) => theme.colorSurface};
      }
    `}

  /* Disabled */
  &:disabled {
    opacity: 0.38;
    cursor: not-allowed;
    filter: none;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// ─── Button component ─────────────────────────────────────────────────────────
/**
 * @param {string}    variant       "filled" | "outline" | "ghost"  (default: "filled")
 * @param {string}    size          "sm" | "md" | "lg"              (default: "md")
 * @param {string}    color         CSS color for the button accent
 * @param {string}    textColor     Override text/icon color (filled variant)
 * @param {string}    label         Button label text (omit for icon-only)
 * @param {ReactNode} icon          Lucide icon element (omit for text-only)
 * @param {string}    iconPosition  "left" | "right"                (default: "left")
 */
const Button = ({
  variant = "filled",
  size = "md",
  color,
  textColor,
  label,
  icon,
  iconPosition = "left",
  children,
  ...rest
}) => {
  const iconOnly = !!icon && !label && !children;
  const resolvedColor = color || "var(--md-sys-color-primary)";
  const resolvedIcon = icon
    ? React.cloneElement(icon, {
        size: SIZE[size]?.iconSize || 16,
        strokeWidth: 2,
      })
    : null;

  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $color={resolvedColor}
      $textColor={textColor}
      $iconOnly={iconOnly}
      {...rest}
    >
      {resolvedIcon && iconPosition === "left" && (
        <IconWrapper>{resolvedIcon}</IconWrapper>
      )}
      {label || children || null}
      {resolvedIcon && iconPosition === "right" && (
        <IconWrapper>{resolvedIcon}</IconWrapper>
      )}
    </StyledButton>
  );
};

export default Button;
